import { type HTMLAttributes, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { ProblemSolverFormMetadata } from 'autogen/swagger/comai';
import { NoneOfTheAbove, ProblemSolverSubmit } from 'autogen/translation-keys/trans-website-comai';
import { Button } from 'components/Button';
import ControlledCheckbox from 'components/shared/inputs/checkbox';
import { sendMessage } from '../shared/helpers';
import { UserMessageStyles } from '../shared/styles';

type ProblemSolverFormProps = HTMLAttributes<HTMLFormElement> &
    Pick<ProblemSolverFormMetadata, 'statements' | 'choices'>;

type CheckedState = Record<string, boolean>;

const StyledForm = styled.form`
    ${UserMessageStyles}

    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing8};
`;

const Checkbox = styled(ControlledCheckbox)`
    margin-bottom: ${({ theme }) => theme.Spacing8};
`;

const StyledButton = styled(Button)`
    margin-top: ${({ theme }) => theme.Spacing16};
    width: 100%;
`;

const StyledFieldset = styled.fieldset`
    &:after {
        display: none;
    }
`;

const NONE_OF_THE_ABOVE_KEY = 'none-of-the-above';

function useCheckboxState(statements: string[]) {
    const [checked, setChecked] = useState<CheckedState>(() => {
        const initial: CheckedState = {};
        statements.forEach((statement) => {
            initial[statement] = false;
        });
        initial[NONE_OF_THE_ABOVE_KEY] = false;
        return initial;
    });

    const handleCheckboxChange = useCallback(
        (name: string) => {
            setChecked((prev) => {
                if (name === NONE_OF_THE_ABOVE_KEY) {
                    const updated: CheckedState = {};
                    statements.forEach((s) => {
                        updated[s] = false;
                    });
                    updated[NONE_OF_THE_ABOVE_KEY] = !prev[NONE_OF_THE_ABOVE_KEY];
                    return updated;
                }
                return {
                    ...prev,
                    [name]: !prev[name],
                    [NONE_OF_THE_ABOVE_KEY]: false
                };
            });
        },
        [statements]
    );

    return { checked, handleCheckboxChange };
}

export default function ProblemSolverForm(props: ProblemSolverFormProps) {
    const { statements, choices, className } = props;
    const { checked, handleCheckboxChange } = useCheckboxState(statements);
    const [hasSubmitted, setHasSubmitted] = useState(choices.length > 0);
    const { t } = useTranslation();

    const submitAnswers = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setHasSubmitted(true);
        const answers = statements.map((statement) => `${statement}: ${checked[statement]}`);
        sendMessage(answers.join(', '), { hideMessage: true });
    };

    const renderCheckboxes = () => (
        <>
            {statements.map((question, index) => {
                const isChecked = choices.length ? choices[index] : checked[question];

                return (
                    <Checkbox
                        name={question}
                        label={question}
                        key={question}
                        checked={isChecked}
                        onChange={() => handleCheckboxChange(question)}
                    />
                );
            })}
            {(!hasSubmitted || checked[NONE_OF_THE_ABOVE_KEY]) && (
                <Checkbox
                    name={NONE_OF_THE_ABOVE_KEY}
                    label={t(NoneOfTheAbove)}
                    checked={checked[NONE_OF_THE_ABOVE_KEY]}
                    onChange={() => handleCheckboxChange(NONE_OF_THE_ABOVE_KEY)}
                />
            )}
        </>
    );

    return (
        <StyledForm onSubmit={submitAnswers} className={className}>
            <StyledFieldset disabled={hasSubmitted}>
                {renderCheckboxes()}
                {!hasSubmitted && <StyledButton>{t(ProblemSolverSubmit)}</StyledButton>}
            </StyledFieldset>
        </StyledForm>
    );
}
